import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "../utils/axiosConfig";
import { clearAccessToken, setAccessToken as setTokenInMemory } from "./tokenStore";
import { getTokenStatus } from "../utils/isTokenExpired";

const AuthContext = createContext(null);
const REFRESH_MARGIN_MS = 2 * 60 * 1000;
const SESSION_CHANNEL_NAME = "infocarga-session";

export const AuthProvider = ({ children }) => {
    const [accessToken, setAccessTokenState] = useState(null);
    const [status, setStatus] = useState("loading");
    const refreshPromiseRef = useRef(null);
    const refreshTimerRef = useRef(null);
    const sessionChannelRef = useRef(null);

    const updateAccessToken = useCallback((token) => {
        setTokenInMemory(token);
        setAccessTokenState(token || null);

        // Puente temporal para los módulos antiguos que aún leen `tkn` directamente.
        // Se retirará cuando esos consumidores (especialmente sockets) usen useAuth.
        if (token) localStorage.setItem("tkn", token);
        else localStorage.removeItem("tkn");
        sessionChannelRef.current?.postMessage({ type: "token", token: token || null });
    }, []);

    const clearSession = useCallback(() => {
        if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
        clearAccessToken();
        setAccessTokenState(null);
        localStorage.removeItem("tkn");
        localStorage.removeItem("tokenExpiration");
        localStorage.removeItem("lastActivityTimeTokenCreate");
        setStatus("anonymous");
        sessionChannelRef.current?.postMessage({ type: "logout" });
    }, []);

    const refreshToken = useCallback(async () => {
        if (refreshPromiseRef.current) return refreshPromiseRef.current;

        refreshPromiseRef.current = axios
            .get(process.env.REACT_APP_SERVER + "/actualizartoken", {
                withCredentials: true,
                skipAuthRefresh: true,
            })
            .then((response) => {
                const newToken = response.data?.nuevotoken;
                if (!newToken) throw new Error("El servidor no devolvio un token actualizado");
                updateAccessToken(newToken);
                setStatus("authenticated");
                return newToken;
            })
            .catch((error) => {
                clearSession();
                throw error;
            })
            .finally(() => {
                refreshPromiseRef.current = null;
            });

        return refreshPromiseRef.current;
    }, [clearSession, updateAccessToken]);

    const confirmToken = useCallback(async () => {
        const response = await axios.get(process.env.REACT_APP_SERVER + "/confirmartoken");
        if (response.data?.success !== true) throw new Error("Token invalido");
        return true;
    }, []);

    const loginWithToken = useCallback((token) => {
        updateAccessToken(token);
        localStorage.setItem("auth:lastActivity", String(Date.now()));
        setStatus("authenticated");
    }, [updateAccessToken]);

    const logout = useCallback(async () => {
        try {
            await axios.get(process.env.REACT_APP_SERVER + "/logout", {
                withCredentials: true,
                skipAuthRefresh: true,
            });
        } catch (error) {
            console.error("No fue posible notificar el cierre de sesion:", error);
        } finally {
            clearSession();
        }
    }, [clearSession]);

    useEffect(() => {
        const applyTokenFromAnotherTab = (token) => {
            setTokenInMemory(token);
            setAccessTokenState(token || null);
            setStatus(token ? "authenticated" : "anonymous");
        };

        if (typeof BroadcastChannel !== "undefined") {
            const channel = new BroadcastChannel(SESSION_CHANNEL_NAME);
            sessionChannelRef.current = channel;
            channel.onmessage = ({ data }) => {
                if (data?.type === "token") applyTokenFromAnotherTab(data.token);
                if (data?.type === "logout") applyTokenFromAnotherTab(null);
            };
        }

        const handleStorage = (event) => {
            if (event.key === "tkn") applyTokenFromAnotherTab(event.newValue);
        };
        window.addEventListener("storage", handleStorage);

        return () => {
            window.removeEventListener("storage", handleStorage);
            sessionChannelRef.current?.close();
            sessionChannelRef.current = null;
        };
    }, []);

    useEffect(() => {
        const legacyToken = localStorage.getItem("tkn");
        if (legacyToken && !getTokenStatus(legacyToken).isExpired) {
            updateAccessToken(legacyToken);
            axios.get(process.env.REACT_APP_SERVER + "/confirmartoken")
                .then((response) => {
                    if (response.data?.success !== true) throw new Error("Token invalido");
                    setStatus("authenticated");
                })
                .catch(() => refreshToken().catch(() => {}));
            return;
        }

        refreshToken().catch(() => {
            // La ausencia de una cookie de sesion valida representa un visitante anonimo.
        });
    }, [refreshToken, updateAccessToken]);

    useEffect(() => {
        if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
        if (!accessToken) return undefined;

        const tokenStatus = getTokenStatus(accessToken, 0);
        if (!tokenStatus.expiresAt) return undefined;
        const delay = Math.max(0, tokenStatus.remainingMs - REFRESH_MARGIN_MS);
        refreshTimerRef.current = setTimeout(() => refreshToken().catch(() => {}), delay);

        return () => {
            if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
        };
    }, [accessToken, refreshToken]);

    const value = useMemo(() => ({
        accessToken,
        status,
        isAuthenticated: status === "authenticated",
        loginWithToken,
        logout,
        refreshToken,
        confirmToken,
        clearSession,
    }), [accessToken, status, loginWithToken, logout, refreshToken, confirmToken, clearSession]);

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) throw new Error("useAuth debe usarse dentro de AuthProvider");
    return context;
};

export const withAuth = (Component) => function WithAuth(props) {
    const auth = useAuth();
    return <Component {...props} auth={auth} />;
};
